import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { roomsAPI, recordingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useRoom } from '../context/RoomContext';
import RoomChat from '../components/RoomChat';
import './RoomDetail.css';

const RoomDetail = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { leaveRoom } = useRoom();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('participants');
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [showStartMeeting, setShowStartMeeting] = useState(false);
  const [newParticipant, setNewParticipant] = useState({ email: '', role: 'member' });
  const [meetingData, setMeetingData] = useState({ title: '', description: '' });
  const [meetings, setMeetings] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [invitations, setInvitations] = useState([]);
  const [showCreateInvite, setShowCreateInvite] = useState(false);
  const [newInvite, setNewInvite] = useState({ role: 'member', maxUses: 0, expiresAt: '' });

  // Recordings state
  const [recordings, setRecordings] = useState([]);
  const [recordingsLoading, setRecordingsLoading] = useState(false);

  useEffect(() => {
    loadRoom();
  }, [roomId]);

  const loadRoom = async () => {
    try {
      setLoading(true);
      const response = await roomsAPI.getRoom(roomId);
      setRoom(response.data);
      setEditData({
        name: response.data.name,
        description: response.data.description,
        isPublic: response.data.isPublic,
        maxParticipants: response.data.maxParticipants
      });
      loadMeetingHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMeetingHistory = async () => {
    try {
      const response = await roomsAPI.getMeetingHistory(roomId);
      setMeetings(response.data);
    } catch (err) {
      console.error('Failed to load meeting history:', err);
    }
  };

  const loadInvitations = async () => {
    try {
      const response = await roomsAPI.getInvitations(roomId);
      setInvitations(response.data);
    } catch (err) {
      console.error('Failed to load invitations:', err);
    }
  };

  const loadRecordings = async () => {
    setRecordingsLoading(true);
    try {
      const response = await recordingsAPI.getRoomRecordings(roomId);
      if (response.success) {
        setRecordings(response.data);
      }
    } catch (err) {
      console.error('Failed to load recordings:', err);
    } finally {
      setRecordingsLoading(false);
    }
  };

  const handleDeleteRecording = async (recordingId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet enregistrement ?')) {
      return;
    }
    try {
      await recordingsAPI.deleteRecording(recordingId);
      setRecordings(recordings.filter(r => r.id !== recordingId));
    } catch (err) {
      console.error('Error deleting recording:', err);
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return '00:00:00';
    return duration;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    try {
      await roomsAPI.createInvitation(roomId, newInvite);
      setShowCreateInvite(false);
      setNewInvite({ role: 'member', maxUses: 0, expiresAt: '' });
      loadInvitations();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette invitation ?')) return;
    try {
      await roomsAPI.deleteInvitation(roomId, invitationId);
      loadInvitations();
    } catch (err) {
      setError(err.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Lien copié dans le presse-papiers !');
  };

  const handleUpdateRoom = async (e) => {
    e.preventDefault();
    try {
      await roomsAPI.updateRoom(roomId, editData);
      setIsEditing(false);
      loadRoom();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddParticipant = async (e) => {
    e.preventDefault();
    try {
      await roomsAPI.addParticipant(roomId, newParticipant.email, newParticipant.role);
      setShowAddParticipant(false);
      setNewParticipant({ email: '', role: 'member' });
      loadRoom();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveParticipant = async (userId) => {
    if (!window.confirm('Voulez-vous vraiment retirer ce participant ?')) return;
    try {
      await roomsAPI.removeParticipant(roomId, userId);
      loadRoom();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await roomsAPI.updateParticipantRole(roomId, userId, newRole);
      loadRoom();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStartMeeting = async (e) => {
    e.preventDefault();
    try {
      const response = await roomsAPI.startMeeting(roomId, meetingData.title, meetingData.description);
      setShowStartMeeting(false);
      setMeetingData({ title: '', description: '' });
      // Check if user is the host - if so, join directly
      const isHost = room?.isOwner || room?.isAdmin;
      if (isHost) {
        // Host can join directly
        navigate(`/room/${roomId}`);
      } else {
        // Redirect to waiting room instead of directly joining
        navigate(`/rooms/${roomId}/waiting`);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEndMeeting = async () => {
    try {
      await roomsAPI.endMeeting(roomId);
      loadRoom();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleNotifications = async () => {
    try {
      const currentParticipant = room.participants?.find(p => p.id === room.currentUserId);
      await roomsAPI.toggleNotifications(roomId, !currentParticipant?.notificationsEnabled);
      loadRoom();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRoom = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette room ? Cette action est irréversible.')) return;
    try {
      await roomsAPI.deleteRoom(roomId);
      navigate('/rooms');
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="loading">Chargement...</div>;

  if (!room) return <div className="error">Room non trouvée</div>;

  return (
    <div className="room-detail-page">
      <div className="room-header">
        <button className="back-btn" onClick={() => navigate('/rooms')}>
          ← Retour
        </button>
        <div className="room-title">
          {isEditing ? (
            <form onSubmit={handleUpdateRoom} className="inline-edit">
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              />
              <button type="submit" className="btn-primary btn-sm">Sauvegarder</button>
              <button type="button" className="btn-secondary btn-sm" onClick={() => setIsEditing(false)}>Annuler</button>
            </form>
          ) : (
            <>
              <h1>{room.name}</h1>
              {room.isOwner && (
                <button className="btn-icon" onClick={() => setIsEditing(true)}>✏️</button>
              )}
            </>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="room-tabs">
        <button 
          className={`tab ${activeTab === 'participants' ? 'active' : ''}`}
          onClick={() => setActiveTab('participants')}
        >
          Participants ({room.participants?.length || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'meetings' ? 'active' : ''}`}
          onClick={() => setActiveTab('meetings')}
        >
          Réunions
        </button>
        <button 
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Paramètres
        </button>
        {(room.isOwner || room.isAdmin) && (
          <button 
            className={`tab ${activeTab === 'invitations' ? 'active' : ''}`}
            onClick={() => { setActiveTab('invitations'); loadInvitations(); }}
          >
            Invitations
          </button>
        )}
        <button 
          className={`tab ${activeTab === 'recordings' ? 'active' : ''}`}
          onClick={() => { setActiveTab('recordings'); loadRecordings(); }}
        >
          Enregistrements
        </button>
        <button 
          className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          💬 Chat
        </button>
      </div>

      <div className="room-content">
        {activeTab === 'participants' && (
          <div className="participants-tab">
            {room.isOwner && (
              <div className="tab-actions">
                <button className="btn-primary" onClick={() => setShowAddParticipant(true)}>
                  + Ajouter un participant
                </button>
              </div>
            )}
            
            <div className="participants-list">
              {room.participants?.map(participant => (
                <div key={participant.id} className="participant-card">
                  <div className="participant-info">
                    <div className="participant-avatar">
                      {participant.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="participant-name">{participant.name}</div>
                      <div className="participant-email">{participant.email}</div>
                    </div>
                  </div>
                  <div className="participant-role">
                    <span className={`role-badge ${participant.role}`}>
                      {participant.role === 'owner' ? 'Propriétaire' : 
                       participant.role === 'admin' ? 'Administrateur' : 'Membre'}
                    </span>
                  </div>
                  {room.isOwner && participant.role !== 'owner' && (
                    <div className="participant-actions">
                      <select 
                        value={participant.role}
                        onChange={(e) => handleUpdateRole(participant.id, e.target.value)}
                      >
                        <option value="member">Membre</option>
                        <option value="admin">Administrateur</option>
                      </select>
                      <button 
                        className="btn-danger btn-sm"
                        onClick={() => handleRemoveParticipant(participant.id)}
                      >
                        Retirer
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'meetings' && (
          <div className="meetings-tab">
            {(room.isOwner || room.isAdmin) && (
              <div className="tab-actions">
                <button className="btn-primary" onClick={() => setShowStartMeeting(true)}>
                  📹 Démarrer une réunion
                </button>
              </div>
            )}

            <div className="meetings-list">
              {meetings.length === 0 ? (
                <p className="empty-message">Aucune réunion dans cette room.</p>
              ) : (
                meetings.map(meeting => (
                  <div key={meeting.id} className="meeting-card">
                    <div className="meeting-info">
                      <h3>{meeting.title}</h3>
                      <p>{new Date(meeting.started_at).toLocaleString()}</p>
                      {meeting.description && <p>{meeting.description}</p>}
                    </div>
                    <div className="meeting-status">
                      <span className={`status-badge ${meeting.status}`}>
                        {meeting.status === 'active' ? 'En cours' : 'Terminée'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-tab">
            <div className="settings-section">
              <h3>Paramètres de la room</h3>
              {room.isOwner ? (
                <form onSubmit={handleUpdateRoom} className="settings-form">
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={editData.isPublic}
                        onChange={(e) => setEditData({ ...editData, isPublic: e.target.checked })}
                      />
                      Room publique
                    </label>
                  </div>
                  <div className="form-group">
                    <label>Participants max</label>
                    <input
                      type="number"
                      value={editData.maxParticipants}
                      onChange={(e) => setEditData({ ...editData, maxParticipants: parseInt(e.target.value) })}
                      min={2}
                      max={100}
                    />
                  </div>
                  <button type="submit" className="btn-primary">Sauvegarder</button>
                </form>
              ) : (
                <div className="notification-settings">
                  <h4>Notifications</h4>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={room.participants?.find(p => p.id === room.currentUserId)?.notificationsEnabled}
                      onChange={handleToggleNotifications}
                    />
                    Recevoir des notifications quand une réunion démarre
                  </label>
                </div>
              )}
            </div>

            <div className="settings-section danger-zone">
              <h3>Zone danger</h3>
              {room.isOwner ? (
                <button className="btn-danger" onClick={handleDeleteRoom}>
                  Supprimer la room
                </button>
              ) : (
                <button className="btn-secondary" onClick={() => {
                  if (window.confirm('Voulez-vous vraiment quitter cette room ?')) {
                    leaveRoom().then(() => navigate('/rooms'));
                  }
                }}>
                  Quitter la room
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 'invitations' && (room.isOwner || room.isAdmin) && (
          <div className="invitations-tab">
            <div className="tab-actions">
              <button className="btn-primary" onClick={() => setShowCreateInvite(true)}>
                + Créer un lien d'invitation
              </button>
            </div>

            <div className="invitations-list">
              {invitations.length === 0 ? (
                <p className="empty-message">Aucun lien d'invitation. Créez-en un pour inviter des participants.</p>
              ) : (
                invitations.map(invite => (
                  <div key={invite.id} className="invitation-card">
                    <div className="invitation-info">
                      <div className="invitation-role">
                        Rôle: <span className={`role-badge ${invite.role}`}>
                          {invite.role === 'admin' ? 'Administrateur' : 'Membre'}
                        </span>
                      </div>
                      <div className="invitation-stats">
                        <span>Utilisations: {invite.usesCount}/{invite.maxUses || '∞'}</span>
                        {invite.expiresAt && <span>Expire: {new Date(invite.expiresAt).toLocaleDateString()}</span>}
                      </div>
                      <div className="invitation-link">
                        <input type="text" value={invite.inviteLink} readOnly />
                        <button className="btn-secondary btn-sm" onClick={() => copyToClipboard(invite.inviteLink)}>
                          Copier
                        </button>
                      </div>
                    </div>
                    <button 
                      className="btn-danger btn-sm"
                      onClick={() => handleDeleteInvitation(invite.id)}
                    >
                      Supprimer
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'recordings' && (
          <div className="recordings-tab">
            <div className="recordings-list">
              {recordingsLoading ? (
                <div className="loading">Chargement des enregistrements...</div>
              ) : recordings.length === 0 ? (
                <p className="empty-message">Aucun enregistrement dans cette salle.</p>
              ) : (
                recordings.map((recording, index) => (
                  <div key={recording.id ? `recording-${recording.id}` : `recording-index-${index}`} className="recording-card">
                    <div className="recording-info">
                      <h3>{recording.title || 'Enregistrement sans titre'}</h3>
                      <p className="recording-date">📅 {formatDate(recording.dateEnregist)}</p>
                      <p className="recording-duration">⏱️ Durée: {formatDuration(recording.duree)}</p>
                    </div>
                    <div className="recording-actions">
                      <button
                        className="btn-danger btn-sm"
                        onClick={() => handleDeleteRecording(recording.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="chat-tab">
            <RoomChat roomId={roomId} />
          </div>
        )}
      </div>

      {/* Add Participant Modal */}
      {showAddParticipant && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Ajouter un participant</h2>
            <form onSubmit={handleAddParticipant}>
              <div className="form-group">
                <label>Email du participant *</label>
                <input
                  type="email"
                  value={newParticipant.email}
                  onChange={(e) => setNewParticipant({ ...newParticipant, email: e.target.value })}
                  required
                  placeholder="email@exemple.com"
                />
              </div>
              <div className="form-group">
                <label>Rôle</label>
                <select
                  value={newParticipant.role}
                  onChange={(e) => setNewParticipant({ ...newParticipant, role: e.target.value })}
                >
                  <option value="member">Membre</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAddParticipant(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Start Meeting Modal */}
      {showStartMeeting && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Démarrer une réunion</h2>
            <form onSubmit={handleStartMeeting}>
              <div className="form-group">
                <label>Titre de la réunion *</label>
                <input
                  type="text"
                  value={meetingData.title}
                  onChange={(e) => setMeetingData({ ...meetingData, title: e.target.value })}
                  required
                  placeholder="Titre de votre réunion"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={meetingData.description}
                  onChange={(e) => setMeetingData({ ...meetingData, description: e.target.value })}
                  rows={3}
                  placeholder="Description optionnelle"
                />
              </div>
              <p className="info-text">
                Les participants de cette room recevront un email de notification.
              </p>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowStartMeeting(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Démarrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Invitation Modal */}
      {showCreateInvite && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Créer un lien d'invitation</h2>
            <form onSubmit={handleCreateInvitation}>
              <div className="form-group">
                <label>Rôle du participant</label>
                <select
                  value={newInvite.role}
                  onChange={(e) => setNewInvite({ ...newInvite, role: e.target.value })}
                >
                  <option value="member">Membre</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <div className="form-group">
                <label>Nombre maximum d'utilisations (0 = illimité)</label>
                <input
                  type="number"
                  value={newInvite.maxUses}
                  onChange={(e) => setNewInvite({ ...newInvite, maxUses: parseInt(e.target.value) })}
                  min={0}
                />
              </div>
              <div className="form-group">
                <label>Date d'expiration (optionnel)</label>
                <input
                  type="date"
                  value={newInvite.expiresAt}
                  onChange={(e) => setNewInvite({ ...newInvite, expiresAt: e.target.value })}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateInvite(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomDetail;
